import { styled } from '@mui/material';
import React from 'react';

import { linkable } from '../../routing';
import { AppLinkOptions, asLinkProps } from '../../routing/links/linkOptions';
import { bodySmallSemiBold, colorSystem, focusVisibleRing } from '../../theme';
import { Flexbox } from '../Flexbox';
import { Text } from '../Text';

export interface PageHeaderProps {
    title: React.ReactNode;
    eyebrow?: string | { label: string; link: AppLinkOptions };
    actions?: React.ReactNode;
    children?: React.ReactNode;
    style?: React.CSSProperties;
}

/**
 * The header of a details page: an optional eyebrow over the title, `children` as lines under it (description,
 * metadata, status), and `actions` on the right. An eyebrow with a `link` leads back to the parent page.
 */
export function PageHeader({ title, eyebrow, actions, children, style }: PageHeaderProps): JSX.Element {
    return (
        <Flexbox
            gap="16px"
            style={style}
            sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}
        >
            <Flexbox gap="4px" sx={{ flexDirection: 'column', minWidth: 0 }}>
                {typeof eyebrow === 'string' && <EyebrowText>{eyebrow}</EyebrowText>}
                {typeof eyebrow === 'object' && (
                    <EyebrowLink {...asLinkProps(eyebrow.link)}>{eyebrow.label}</EyebrowLink>
                )}
                <Text variant="h1">{title}</Text>
                {children}
            </Flexbox>
            <Flexbox gap="8px" sx={{ alignItems: 'center', flexShrink: 0 }}>
                <React.Suspense fallback={null}>{actions}</React.Suspense>
            </Flexbox>
        </Flexbox>
    );
}

const eyebrowStyle = {
    ...bodySmallSemiBold,
    color: colorSystem.neutral[6],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
} as const;

const EyebrowText = styled('span')(eyebrowStyle);

const EyebrowLink = linkable(
    styled('a')(
        {
            ...eyebrowStyle,
            alignSelf: 'flex-start',
            textDecoration: 'none',
            borderRadius: 4,
            '&:hover': { color: colorSystem.neutral[9] },
        },
        focusVisibleRing,
    ),
);
